import { Injectable } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';

import { catchError } from 'rxjs/operators';
import { combineLatest, Observable, of, map } from 'rxjs';

import { MediaSubscriptionApi } from './models/media-subscription.api.model';
import { HttpVerbsEnum } from '../shared/enums/http-verbs.enum';
import { QuotaInformation } from './models/quota-information.api.model';
import { QuotaHistoryInformation } from './models/quota-history-information.api.model';
import { SubscriptionContentTimeslot } from './models/subscription-content-timeslot.api.model';
import { TopSubscriptionApiModel } from './models/top-subscription.api.model';
import { NeptuneApi } from '../genericApi/neptune.api';
import { BackfillRequestApiModel } from './models/backfill-request.api.model';
import { ContentOverTimeDurationTypeEnum } from './enums/content-over-time-duration-type.enum';
import { DisplayGroupPatchModel } from '../displayGroups/models/display-group-patch.api.model';
import { MultiBaseAbstractApi } from '@api/genericApi/multi-base-abstract.api';

@Injectable()
export class MediaSubscriptionApiService extends MultiBaseAbstractApi<MediaSubscriptionApi, MediaSubscriptionApi> {

    constructor(private neptuneApi: NeptuneApi) {
        super();
    }


    getList(): Observable<MediaSubscriptionApi[]> {
        const slug = '/socialsubscriptions';

        return this.neptuneApi.api(
            slug,
            null,
            HttpVerbsEnum.GET
        );
    }


    getById(id: number): Observable<MediaSubscriptionApi> {
        const slug = `/socialsubscriptions/${id}`;

        return this.neptuneApi.api(
            slug,
            null,
            HttpVerbsEnum.GET
        );
    }

    /** @TODO NEP-32488 - fix with b/e batch */
    getByIds(ids: number[]): Observable<MediaSubscriptionApi[]> {
        const apiCalls: Observable<MediaSubscriptionApi>[] = [];

        [...new Set(ids)].forEach(id => {
            apiCalls.push(this.getById(id));
        });

        return combineLatest(apiCalls);
    }

    create(subscription: MediaSubscriptionApi): Observable<number> {
        const slug = '/socialsubscriptions';

        return this.neptuneApi.api(
            slug,
            subscription,
            HttpVerbsEnum.POST
        );
    }

    /** @TODO NEP-32489 - change endpoint to return id */
    update(subscription: MediaSubscriptionApi): Observable<number> {
        const slug = `/socialsubscriptions/${subscription.id}`;

        return this.neptuneApi.api(
            slug,
            subscription,
            HttpVerbsEnum.PUT
        ).pipe(
            map(() => subscription.id) // @HACK
        );
    }

    deleteById(id: number): Observable<number> {
        const slug = `/socialsubscriptions/${id}`;

        return this.neptuneApi.api(
            slug,
            null,
            HttpVerbsEnum.DELETE
        );
    }



    /**
     * Adds and/or removes subscriptions to/from provided group
     * @param addIds Subscriptions to add to group
     * @param removeIds Subscriptions to remove from group
     * @param groupId
     */
    updateDisplayGroup(addIds: number[], removeIds: number[], groupId: number): Observable<void> {
        const slug: string = `/socialsubscriptions/displaygroups/${groupId}`;

        const patchModel: DisplayGroupPatchModel = {
            addResourceIds: addIds,
            removeResourceIds: removeIds
        };

        return this.neptuneApi.api<void>(
            slug,
            patchModel,
            HttpVerbsEnum.PATCH
        );
    }

    /**
     * Removes group ID from provided subscriptions
     * @param removeIds subscriptions to remove
     */
    removeDisplayGroup(removeIds: number[]): Observable<void> {
        const slug: string = '/socialsubscriptions/displaygroups';

        const patchModel: DisplayGroupPatchModel = {
            addResourceIds: [],
            removeResourceIds: removeIds
        };

        return this.neptuneApi.api<void>(
            slug,
            patchModel,
            HttpVerbsEnum.PATCH
        );
    }


    updateFavorites(addIds: number[], removeIds: number[]): Observable<void> {
        const slug: string = '/socialsubscriptions/displaygroups/favorites';

        const patchModel: DisplayGroupPatchModel = {
            addResourceIds: addIds,
            removeResourceIds: removeIds
        };

        return this.neptuneApi.api<void>(
            slug,
            patchModel,
            HttpVerbsEnum.PATCH
        );
    }


    // Returns one QuotaInformation entry per quota type (see quotaType), distinguishing e.g. media subscription vs social listening quotas
    getAccountQuotaInfo(monthTimestamp?: number): Observable<QuotaInformation[]> {
        const slug = '/quotagroups/accounts/mine';
        const query = monthTimestamp ? {monthTimestamp}: {};

        return this.neptuneApi.api<QuotaInformation[]>(
            slug,
            null,
            HttpVerbsEnum.GET,
            query
        ).pipe(
            catchError(error => {
                if (error instanceof HttpErrorResponse && (<HttpErrorResponse> error).status === 404) {
                    return of([]);
                }
                throw error;
            })
        );
    }

    getAccountQuotaHistoryInfo(monthTimestamp?: number): Observable<QuotaHistoryInformation[]> {
        const slug = '/socialsubscriptions/quotausage';
        const query = monthTimestamp ? {monthTimestamp}: {};

        return this.neptuneApi.api<QuotaHistoryInformation[]>(
            slug,
            null,
            HttpVerbsEnum.GET,
            query
        ).pipe(
            catchError(error => {
                if (error instanceof HttpErrorResponse && (<HttpErrorResponse> error).status === 404) {
                    return of(null);
                }
                throw error;
            })
        );
    }


    // Admin
    getAnyAccountQuotaInfo(accountId: number): Observable<QuotaInformation> {
        const slug = `/quotagroups/accounts/${accountId}`;

        return this.neptuneApi.api<QuotaInformation>(
            slug,
            null,
            HttpVerbsEnum.GET
        ).pipe(
            catchError(error => {
                if (error instanceof HttpErrorResponse && (<HttpErrorResponse> error).status === 404) {
                    return of(null);
                }
                throw error;
            })
        );
    }

    // Admin
    getMediaSubscriptionQuotaInfo(accountId: number): Observable<QuotaInformation> {
        const slug = `/quotagroups/account/${accountId}/socialsubscription`;

        return this.neptuneApi.api<QuotaInformation>(
            slug,
            null,
            HttpVerbsEnum.GET
        ).pipe(
            catchError(error => {
                if (error instanceof HttpErrorResponse && (<HttpErrorResponse> error).status === 404) {
                    return of(null);
                }
                throw error;
            })
        );
    }

    // Admin
    getSocialListeningQuotaInfo(accountId: number): Observable<QuotaInformation> {
        const slug = `/quotagroups/account/${accountId}/socialtopic`;

        return this.neptuneApi.api<QuotaInformation>(
            slug,
            null,
            HttpVerbsEnum.GET
        ).pipe(
            catchError(error => {
                if (error instanceof HttpErrorResponse && (<HttpErrorResponse> error).status === 404) {
                    return of(null);
                }
                throw error;
            })
        );
    }

    // Admin
    updateQuotaInfo(quota: QuotaInformation): Observable<QuotaInformation> {
        const slug = `/quotagroups/${quota.id}`;

        return this.neptuneApi.api<QuotaInformation>(
            slug,
            quota,
            HttpVerbsEnum.PUT
        );
    }

    // Admin
    createQuotaInfo(quota: QuotaInformation): Observable<number> {
        const slug: string = '/quotagroups/';

        return this.neptuneApi.api<number>(
            slug,
            quota,
            HttpVerbsEnum.POST
        );
    }

    // Admin
    deleteQuotaInfo(id: number): Observable<QuotaInformation> {
        const slug = `/quotagroups/${id}`;

        return this.neptuneApi.api<QuotaInformation>(
            slug,
            null,
            HttpVerbsEnum.DELETE
        );
    }

    /**
     * Gets the N top subscriptions based on Quota Usage
     * @param count Number of subscriptions to get
     */
    getTopSubscriptions(count: number, isForAgent: boolean): Observable<TopSubscriptionApiModel[]> {
        const slug = '/socialsubscriptions/topsubscriptions';

        return this.neptuneApi.api<TopSubscriptionApiModel[]>(
            slug,
            null,
            HttpVerbsEnum.GET,
            {
                limit: count,
                socialOnly: true,
                isForAgent: isForAgent
            }
        );
    }

    /**
     * Gets the content over time for a specific subscription
     * If no id is given, then get the global account information
     * @param id A Subscription ID
     */
    getContentOverTime(
        id?: number,
        isForAgent: boolean = false,
        duration: ContentOverTimeDurationTypeEnum = ContentOverTimeDurationTypeEnum.CURRENT_MONTH,
        sliceInHours?: number
    ): Observable<SubscriptionContentTimeslot[]> {
        const query =             {
            socialOnly: true,
            isForAgent: isForAgent,
            duration: duration,
        };
        if (sliceInHours) {
            query['sliceInHours'] = sliceInHours;
        }

        return this.neptuneApi.api<SubscriptionContentTimeslot[]>(
            id ? `/socialsubscriptions/${id}/contentovertime` : '/socialsubscriptions/contentovertime',
            null,
            HttpVerbsEnum.GET,
            query
        );
    }

    getAgentQuotaEstimate(agent: MediaSubscriptionApi): Observable<any> {
        const slug = '/socialsubscriptions/quotaestimate';

        return this.neptuneApi.api<any>(
            slug,
            agent,
            HttpVerbsEnum.POST
        );
    }

    requestBackfill(subscriptionId: number, backfillRequest: BackfillRequestApiModel): Observable<void> {
        const slug = `/socialsubscriptions/backfill/${subscriptionId}`;

        return this.neptuneApi.api<void>(
            slug,
            backfillRequest,
            HttpVerbsEnum.POST
        );
    }

    estimateBackfillCount(subscriptionId: number, backfillRequest: BackfillRequestApiModel): Observable<number> {
        const slug = `/socialsubscriptions/backfill/${subscriptionId}/estimate`;

        return this.neptuneApi.api<number>(
            slug,
            backfillRequest,
            HttpVerbsEnum.POST
        );
    }
}
